import { useState } from "react";
import { useApp } from "../app-context";
import { listCompanies, listPeople, optionLabel, personEmail, personName, saleName, setCompanyStage, type Company, type Person } from "../data";
import { CompanyLogo } from "../CompanyLogo";
import { VoiceBar } from "../voice/VoiceBar";
import { navigate, type Route } from "../router";
import { usd } from "../stages";
import { ErrorNote, Loading, PageHeader, plural, useLoad } from "../ui";
import { Board } from "./Board";
import { CompanyPanel, NotFoundPanel, PersonPanel, PersonRow } from "./Panels";

const matches = (q: string, ...fields: (string | null | undefined)[]) =>
  !q.trim() || fields.filter(Boolean).join(" ").toLowerCase().includes(q.trim().toLowerCase());

/**
 * The pipeline: one card per company, dragged between stages. People are
 * reached through their company's panel; the few with no company are listed
 * under the board so they can be linked.
 */
export function CompaniesPage({ route }: { route: Extract<Route, { page: "companies" | "person" }> }) {
  const { salesById, sales, config } = useApp();
  const [query, setQuery] = useState("");
  const [owner, setOwner] = useState("");
  const [adding, setAdding] = useState(false);
  const load = useLoad(async () => {
    const [companies, people] = await Promise.all([listCompanies(), listPeople()]);
    return { companies, people };
  }, []);
  // Re-reads companies and people; every panel is drawn from this data, so an
  // open panel shows the change too. Also what VoiceBar's onApplied calls.
  const reload = load.reload;

  if (load.error) return <ErrorNote error={load.error} />;
  if (!load.data) return <Loading />;
  const { companies, people } = load.data;
  const peopleAt = new Map<number, Person[]>();
  for (const p of people) if (p.company_id) peopleAt.set(p.company_id, [...(peopleAt.get(p.company_id) ?? []), p]);
  const personText = (p: Person) => [personName(p), p.title, personEmail(p)];

  const shown = companies.filter((c) =>
    (!owner || String(c.sales_id) === owner) &&
    matches(query, c.name, c.website, optionLabel(config.companySectors, c.sector), ...(peopleAt.get(c.id) ?? []).flatMap(personText)));
  const unlinked = people.filter((p) => !p.company_id || !companies.some((c) => c.id === p.company_id));
  const unlinkedShown = unlinked.filter((p) => matches(query, ...personText(p)));
  const toBoard = () => { setAdding(false); navigate({ page: "companies" }); };
  const toCompanyOf = (p: Person) => {
    const c = p.company_id ? companies.find((x) => x.id === p.company_id) : undefined;
    navigate(c ? { page: "companies", id: c.id } : { page: "person", id: p.id });
  };
  const onPersonSaved = async (p: Person) => { await reload(); toCompanyOf(p); };

  let panel = null;
  if (route.page === "person") {
    const person = people.find((p) => p.id === route.id);
    const company = person?.company_id ? companies.find((c) => c.id === person.company_id) : undefined;
    panel = person
      ? <PersonPanel key={`${person.id}:${person.updated_at}`} person={person} company={company} companies={companies}
          onClose={() => (company ? navigate({ page: "companies", id: company.id }) : toBoard())} onSaved={onPersonSaved} />
      : <NotFoundPanel what="person" onClose={toBoard} />;
  } else if (route.id) {
    const company = companies.find((c) => c.id === route.id);
    if (!company) panel = <NotFoundPanel what="company" onClose={toBoard} />;
    else if (route.addPerson) {
      panel = <PersonPanel key={`new:${company.id}`} person={null} company={company} companies={companies}
        onClose={() => navigate({ page: "companies", id: company.id })} onSaved={onPersonSaved} />;
    } else {
      panel = <CompanyPanel key={`${company.id}:${company.updated_at}`} company={company} people={people} onClose={toBoard}
        onSaved={async (c) => { await reload(); navigate({ page: "companies", id: c.id }); }} />;
    }
  } else if (adding) {
    panel = <CompanyPanel key="new" company={null} people={people} onClose={toBoard}
      onSaved={async (c) => { await reload(); setAdding(false); navigate({ page: "companies", id: c.id }); }} />;
  }

  return (
    <>
      <PageHeader title="Companies" sub={`${plural(companies.length, "company", "companies")} in the pipeline`}>
        <button type="button" className="btn primary" onClick={() => { setAdding(true); navigate({ page: "companies" }); }}>Add company</button>
      </PageHeader>
      <VoiceBar onApplied={() => void reload()} />
      <div className="toolbar">
        <input className="search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search companies, industries, people…" aria-label="Search" />
        <select value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Owner">
          <option value="">All owners</option>
          {sales.filter((s) => !s.disabled).map((s) => <option key={s.id} value={s.id}>{saleName(s)}</option>)}
        </select>
      </div>
      <Board<Company>
        items={shown}
        label={(c) => c.name}
        onMove={async (c, stage) => { await setCompanyStage(c.id, stage); await reload(); }}
        onOpen={(c) => { setAdding(false); navigate({ page: "companies", id: c.id }); }}
        renderCard={(c) => <CompanyCard company={c} people={peopleAt.get(c.id)?.length ?? 0} owner={saleName(c.sales_id ? salesById.get(c.sales_id) : undefined)} />}
      />
      {unlinkedShown.length ? <Unlinked people={unlinkedShown} /> : null}
      {panel}
    </>
  );
}

function CompanyCard({ company: c, people, owner }: { company: Company; people: number; owner: string }) {
  const { config } = useApp();
  const sector = optionLabel(config.companySectors, c.sector);
  return (
    <>
      <span className="kcard-head">
        <CompanyLogo name={c.name} website={c.website} size="md" />
        <span className="kcard-name">
          <strong className="kcard-title">{c.name}</strong>
          <span className="muted small">{[sector, people ? plural(people, "person", "people") : null].filter(Boolean).join(" · ") || " "}</span>
        </span>
      </span>
      <span className="kcard-foot"><span className="kcard-value">{c.deal_value != null ? usd(c.deal_value) : "—"}</span><span className="owner">{owner}</span></span>
    </>
  );
}

function Unlinked({ people }: { people: Person[] }) {
  return (
    <section className="card unlinked" aria-labelledby="unlinked-title">
      <h2 id="unlinked-title">Not linked to a company <span className="count">{people.length}</span></h2>
      <p className="muted small">Open someone to choose their company.</p>
      <ul className="people">
        {people.map((p) => (
          <li key={p.id}><PersonRow person={p} /></li>
        ))}
      </ul>
    </section>
  );
}
